import { VisibilityOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
} from '@mui/material';
import { Base64Image } from '../../../../utils/settings';
import { getPancardImageAction, AddBeneficiaryAction } from '../APIs/action';
import { toast } from 'react-toastify';
import React from 'react';
import { calculateAgesAndFlags } from '../../../Utils/AgeCalc';
import { useNavigate } from 'react-router-dom';
import { InsuranceType } from '../constants';

const ExistingBeneficiaryCard = ({
  beneficiary,
  benefitType,
  owner_type_text,
  selectedCompanyEntity
}) => {
  const navigate = useNavigate();

  const [panImageError, setPanImageError] = React.useState('');
  const [loader, setLoader] = React.useState(false);
  const [openImage, setOpenImage] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState('');
  const [submitLoader, setSubmitLoader] = React.useState(false);

  const handleClose = () => {
    setOpenImage(false);
    setSelectedImage('');
    setPanImageError('');
  };

  const handleViewImage = (id, selectedCompanyEntity) => {
    getPancardImage(id,selectedCompanyEntity );
    !panImageError && setOpenImage(true);
  };
  // Format date of birth
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getPancardImage = async (beneficiary_id,selectedCompanyEntity) => {
    setLoader(true);
    const { error, data } = await getPancardImageAction(beneficiary_id,selectedCompanyEntity);
    setLoader(false);
    if (!error) {
      setSelectedImage(data.pancard_image);
    } else {
      setPanImageError('No Image Found');
    }
  };

  const handleConfirm = async () => {
    // Calculate age from DOB using calculateAgesAndFlags
    const calculatedAge = beneficiary.dob
      ? calculateAgesAndFlags([beneficiary.dob])[0].age
      : '';

    const payload = {
      firm_owner_name: beneficiary.firm_owner_name,
      id_type: beneficiary.id_type,
      id_number: beneficiary.id_number,
      insurance_id: benefitType,
      id_str_base64: beneficiary.id_str_base64 || selectedImage || null,
      file_name: beneficiary.file_name || null,
      full_name: beneficiary.full_name || '',
      dob: beneficiary.dob,
      age: calculatedAge,
      phone_number: beneficiary.phone_number,
      relation: beneficiary.relation,
      firm_type: owner_type_text?.value || '',
      gender: beneficiary.gender || '',
      company_esi_id: selectedCompanyEntity || '',
    };

    setSubmitLoader(true);
    try {
      const response = await AddBeneficiaryAction(payload);
      setSubmitLoader(false);
      const { data, error, message } = response;
      if (!error) {
        toast.success(message || 'Beneficiary added successfully');
        if (benefitType === InsuranceType.LIFE_INSURANCE) {
          navigate('/beneficiary/add-nominee', {
            state: {
              insurance_type: benefitType,
              beneficiary_id: data?.beneficiary_id,
              selectedCompanyEntity: selectedCompanyEntity
            },
          });
        } else if (benefitType === InsuranceType.MEDICAL_INSURANCE) {
          navigate('/beneficiary/detail', {
            state: {
              insurance_type: benefitType,
              selectedCompanyEntity: selectedCompanyEntity
            },
          });
        }
      } else {
        toast.error(message || 'Failed to add beneficiary');
      }
    } catch (error) {
      setSubmitLoader(false);
      toast.error('An error occurred while adding beneficiary');
    }
  };

  return (
    <Box
      sx={{
        border: '1px solid #D9D9D9',
        borderRadius: 3,
        p: 2,
        height: '100%',
        width: '100%',
        maxWidth: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Box>
          <Typography
            variant='body1'
            sx={{ fontWeight: 700, fontSize: '16px', lineHeight: '24px' }}
          >
            Beneficiary Details
          </Typography>
        </Box>
      </Box>
      <Box>
        <DetailField
          label='Name'
          value={beneficiary?.full_name || 'N/A'}
        />
        <DetailField
          label='Date of Birth'
          value={formatDate(beneficiary?.dob)}
        />
        <DetailField
          label='Relation'
          value={beneficiary?.relation || 'N/A'}
        />
        <DetailField
          label='Mobile No'
          value={beneficiary?.phone_number || 'N/A'}
        />
        <DetailField
          label='PAN'
          value={beneficiary?.id_number || 'N/A'}
          handleViewImage={() => handleViewImage(beneficiary?.beneficiary_id, selectedCompanyEntity )}
          panImageError={panImageError}
        />
        <Button
          variant='contained'
          color='primary'
          onClick={handleConfirm}
          disabled={submitLoader}
          sx={{
            textTransform: 'none',
            width: '100%',
          }}
        >
          {submitLoader ? (
            <CircularProgress
              size={20}
              sx={{ color: 'white' }}
            />
          ) : (
            'Confirm'
          )}
        </Button>
      </Box>
      {!panImageError && (
        <DetailFieldWithImage
          handleClose={handleClose}
          openImage={openImage}
          selectedImage={selectedImage}
          loader={loader}
        />
      )}
    </Box>
  );
};
const DetailField = ({ label, value, handleViewImage, panImageError }) => (
  <Box
    display='flex'
    justifyContent='space-between'
    sx={{ py: 1 }}
  >
    <Typography
      fontWeight={700}
      sx={{ color: '#666666', fontSize: '13px', lineHeight: '20px' }}
    >
      {label}
    </Typography>
    <Box>
      <Box
        display='flex'
        justifyContent='space-between'
        mb={1}
      >
        <Typography sx={{ color: '#333333', fontSize: '13px', pr: 1 }}>
          {value}
        </Typography>
        {label === 'PAN' && handleViewImage && (
          <Box sx={{}}>
            <VisibilityOutlined
              onClick={() => handleViewImage && handleViewImage()}
              fontSize='small'
              sx={{ color: '#FFA525', cursor: 'pointer' }}
            />
          </Box>
        )}
      </Box>
      {label === 'PAN' && panImageError && (
        <Typography sx={{ color: '#CC0000', fontSize: '14px' }}>
          {panImageError}
        </Typography>
      )}
    </Box>
  </Box>
);
const DetailFieldWithImage = ({
  openImage,
  selectedImage,
  handleClose,
  loader,
}) => (
  <Dialog
    open={openImage}
    onClose={handleClose}
    maxWidth='sm'
  >
    <DialogContent sx={{ textAlign: 'center' }}>
      {loader ? (
        <CircularProgress sx={{ color: '#FFA525', fontSize: '30px' }} />
      ) : (
        selectedImage && (
          <Base64Image
            base64String={selectedImage}
            style={{ width: '300px', marginBottom: '10px' }}
          />
        )
      )}
    </DialogContent>
  </Dialog>
);

export default ExistingBeneficiaryCard;
